import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLines } from "@/components/primitives";
import { scrollTo } from "@/components/Header";
import { HeroConsole } from "@/components/HeroConsole";
import { useLanguage } from "@/hooks/use-language";

const HERO_IMG =
  "https://images.unsplash.com/photo-1701328778019-e95dedbf5346?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxzbWFydCUyMGZhY3RvcnklMjBpbmR1c3RyaWFsJTIwbWFudWZhY3R1cmluZyUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzg4Njc0NzU4fDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const { t, lang } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const orb1Y = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const orb2Y = useTransform(scrollYProgress, [0, 1], [0, -220]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <motion.img
          src={HERO_IMG}
          alt=""
          style={{ y: imgY }}
          className="w-full h-[120%] object-cover opacity-[0.12] dark:opacity-25"
        />
        <div className="absolute inset-0 bg-background/85 dark:bg-[#0a0a0a]/80" />
        <motion.div
          style={{ y: orb1Y }}
          className="node-pulse absolute top-[22%] right-[14%] w-64 h-64 rounded-full bg-teal-500/15 dark:bg-teal-400/10 blur-3xl"
        />
        <motion.div
          style={{ y: orb2Y }}
          className="node-pulse absolute bottom-[28%] left-[8%] w-48 h-48 rounded-full bg-green-500/15 dark:bg-green-400/10 blur-3xl"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full pt-40 pb-16">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-16 items-center">
        <div>
        <motion.p
          key={`eyebrow-${lang}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-display text-sm lg:text-base font-medium tracking-[0.35em] lg:tracking-[0.15em] lg:whitespace-nowrap uppercase text-teal-600 dark:text-teal-400 mb-8"
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-4xl" data-testid="hero-headline">
          <MaskedLines
            key={`lines-${lang}`}
            delay={0.35}
            lines={[
              t.hero.line1,
              t.hero.line2,
              <span key="l3" className="text-gradient">{t.hero.line3}</span>,
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-base md:text-lg text-muted-foreground"
          data-testid="hero-subcopy"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); scrollTo("#contact"); }}
            data-testid="hero-cta-demo"
            className="group inline-flex items-center gap-2 h-12 px-8 rounded-full bg-gradient-to-r from-teal-500 to-green-500 text-[#0a0a0a] font-semibold text-sm hover:scale-[1.04] transition-transform duration-300"
          >
            {t.bookDemo}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
          </a>
          <a
            href="#benchmark"
            onClick={(e) => { e.preventDefault(); scrollTo("#benchmark"); }}
            data-testid="hero-cta-benchmark"
            className="inline-flex items-center gap-2 h-12 px-8 rounded-full border border-border text-sm font-medium hover:border-teal-500/60 hover:text-teal-600 dark:hover:text-teal-400 transition-colors duration-300"
          >
            {t.hero.ctaBenchmark}
          </a>
        </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 lg:mt-0"
        >
          <HeroConsole />
        </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-20 grid grid-cols-2 lg:grid-cols-4 border-t border-border"
          data-testid="hero-stats"
        >
          {t.hero.stats.map((s, i) => (
            <div
              key={s.label}
              className={`py-6 pr-6 ${i > 0 ? "lg:border-l lg:border-border lg:pl-8" : ""} ${i % 2 === 1 ? "border-l border-border pl-6 lg:pl-8" : ""}`}
            >
              <div className="font-display font-bold text-2xl lg:text-3xl text-foreground">{s.value}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 right-8 hidden lg:flex items-center gap-2 text-muted-foreground"
      >
        <ArrowDown size={14} className="animate-bounce" />
        <span className="text-xs tracking-[0.3em] uppercase">Scroll</span>
      </motion.div>
    </section>
  );
};
